"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var reselect_1 = require("reselect");
var toolkit_1 = require("@reduxjs/toolkit");
var types_1 = require("../../middleware/api/types");
var types_2 = require("./types");
/**
 * Creates the apiAction with actionTypes following the rules set by the middleware api.
 *
 * @param actionTypePrefix the action type prefix (namespace/actionType) such as "spotify/FETCH_ALBUM"
 * @param apiAction the apiAction object to create
 */
function createApiAction(actionTypePrefix, apiAction) {
    var _a;
    return _a = {
            type: types_1.CALL_API
        },
        _a[types_1.CALL_API] = __assign(__assign({}, apiAction), { actionTypes: [
                actionTypePrefix + "_" + types_2.APIActionTypeSuffix.REQUEST,
                actionTypePrefix + "_" + types_2.APIActionTypeSuffix.SUCCESS,
                actionTypePrefix + "_" + types_2.APIActionTypeSuffix.FAILURE,
                actionTypePrefix + "_" + types_2.APIActionTypeSuffix.CANCEL,
            ] }),
        _a;
}
exports.createApiAction = createApiAction;
/**
 * Creates the initial api state for modules.
 *
 * @param initialState the initial state for the data key in the module state
 */
function createApiInitialState(initialState) {
    return {
        data: initialState,
        isLoading: false,
    };
}
exports.createApiInitialState = createApiInitialState;
/**
 * Creates api onRequest reducer.
 *
 */
function createApiOnRequestReducer(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
dataReducer, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
errorReducer) {
    if (dataReducer === void 0) { dataReducer = function (data, action) { return data; }; }
    if (errorReducer === void 0) { errorReducer = function (error, action) { return error; }; }
    return function (state, action) { return (__assign(__assign({}, state), { data: dataReducer(state.data, action), error: errorReducer(state.error, action) })); };
}
exports.createApiOnRequestReducer = createApiOnRequestReducer;
/**
 * Creates api onSuccess reducer.
 *
 */
function createApiOnSuccessReducer(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
dataReducer) {
    if (dataReducer === void 0) { dataReducer = function (data, action) { return action.payload; }; }
    return function (state, action) { return (__assign(__assign({}, state), { data: dataReducer(state.data, action), 
        // If get a response success, clear the error state
        error: null })); };
}
exports.createApiOnSuccessReducer = createApiOnSuccessReducer;
/**
 * Creates api onFailure reducer.
 *
 */
function createApiOnFailureReducer(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
errorReducer) {
    if (errorReducer === void 0) { errorReducer = function (error, action) { return action.payload; }; }
    return function (state, action) { return (__assign(__assign({}, state), { error: errorReducer(state.error, action) })); };
}
exports.createApiOnFailureReducer = createApiOnFailureReducer;
var updateImmutableState = function (draftState, newState, isLoading) {
    // Immer case reducer pattern 1: return a new state
    if (newState !== undefined) {
        return __assign(__assign({}, newState), { isLoading: isLoading });
    }
    // Immer case reducer pattern 2: directly mutate the draft state, return undefined
    // eslint-disable-next-line no-param-reassign
    draftState.isLoading = isLoading;
    return undefined;
};
/**
 * Creates api reducers following the rules set by the middleware api.
 *
 */
function createApiReducer(_a) {
    var _b;
    var actionType = _a.actionType, _c = _a.onRequest, onRequest = _c === void 0 ? createApiOnRequestReducer() : _c, _d = _a.onSuccess, onSuccess = _d === void 0 ? createApiOnSuccessReducer() : _d, _e = _a.onFailure, onFailure = _e === void 0 ? createApiOnFailureReducer() : _e;
    return _b = {},
        _b[actionType + "_" + types_2.APIActionTypeSuffix.REQUEST] = function (state, action) {
            var newState = onRequest(state, action);
            return updateImmutableState(state, newState, true);
        },
        _b[actionType + "_" + types_2.APIActionTypeSuffix.SUCCESS] = function (state, action) {
            // persist the etag, to identify whether the response is not modified or not, as fetch would never get httpcode 304:
            // https://github.com/github/fetch/issues/241
            var etag = action.etag;
            var newState = onSuccess(state, __assign(__assign({}, action), { isNotModified: !!state.etag && state.etag === etag }));
            if (newState) {
                newState.etag = etag;
                delete newState.error;
            }
            else {
                state.etag = etag;
                delete state.error;
            }
            return updateImmutableState(state, newState, false);
        },
        _b[actionType + "_" + types_2.APIActionTypeSuffix.FAILURE] = function (state, action) {
            var newState = onFailure(state, action);
            if (newState) {
                delete newState.etag;
            }
            else {
                delete state.etag;
            }
            return updateImmutableState(state, newState, false);
        },
        _b;
}
exports.createApiReducer = createApiReducer;
/**
 * Creates a api reducer function for a module.
 *
 * @param ApiReducer the apiReducer object containing actionType, onSuccess, onFailure
 * @param initialState
 */
function createApiModuleReducer(apiReducerOptions, initialState) {
    return toolkit_1.createReducer(createApiInitialState(initialState), createApiReducer(apiReducerOptions));
}
exports.createApiModuleReducer = createApiModuleReducer;
/**
 * Creates a custom reducer function for a module.
 *
 * @param reducerMap a map of actionType to reducer function
 * @param initialState
 */
function createCustomModuleReducer(reducerMap, initialState) {
    return toolkit_1.createReducer(initialState, reducerMap);
}
exports.createCustomModuleReducer = createCustomModuleReducer;
/**
 * Creates a selector for apiActions that deal with getting the correct state.
 *
 * @param moduleState the module state object
 * @param stateKey the state key name in the module state object that we are interested in
 */
exports.createApiSelector = function (moduleState, stateKey) {
    return reselect_1.createSelector(moduleState, function (state) { return state[stateKey]; });
};
/**
 * Creates a selector for apiActions that deal with getting the correct state from the data prop.
 *
 * @param moduleState the module state object
 * @param stateKey the state key name in the module state object that we are interested in
 */
exports.createApiDataSelector = function (moduleState, stateKey) {
    return reselect_1.createSelector(moduleState, function (state) { return state[stateKey].data; });
};
//# sourceMappingURL=index.js.map